import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Activity,
  Search,
  Calendar,
  Filter,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Package,
  X,
  FileText,
  Truck,
  PackageCheck,
  RotateCcw,
  Pencil,
  Gift,
  Mail,
  Printer,
  Ban,
  Layers,
  CreditCard,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format, subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import { DateRange } from 'react-day-picker';
import {
  useInventoryMovementsPage,
  type MovementType,
  type MovementFilters,
  type InventoryMovement,
  getMovementColor,
} from '@/hooks/useInventoryMovements';
import { useLocations } from '@/hooks/useLocations';
import { useProducts } from '@/hooks/useProducts';
import { useLocationStore } from '@/store/location';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { useCurrency } from '@/hooks/useCurrency';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { supabase } from '@/lib/supabase';

const MOVEMENTS_PAGE_SIZE = 500;
import { useQueryClient } from '@tanstack/react-query';
import {
  subscribeToInventoryTransactions,
  subscribeToRawInventoryTransactions,
  subscribeToGoodsTransfers,
  subscribeToInventoryTransfers,
  unsubscribe,
} from '@/lib/realtime';
import { useAuth } from '@/hooks/useAuth';
import { EmailReceiptDialog } from '@/components/orders/EmailReceiptDialog';

// Movement type options for filter
const MOVEMENT_TYPE_OPTIONS: { value: MovementType | 'all'; label: string }[] = [
  { value: 'all', label: 'All movements' },
  { value: 'sale', label: 'Sale' },
  { value: 'transfer_placed', label: 'Transfer placed' },
  { value: 'transfer_received', label: 'Transfer received' },
  { value: 'transfer_in_transit', label: 'Transfer in transit' },
  { value: 'inventory_count_gain', label: 'Inventory count (gain)' },
  { value: 'inventory_count_loss', label: 'Inventory count (loss)' },
  { value: 'supplier_order', label: 'Supplier order' },
  { value: 'supplier_return', label: 'Supplier return' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'waste', label: 'Waste/Loss' },
  { value: 'boh_to_foh', label: 'BOH to FOH' },
  { value: 'foh_to_boh', label: 'FOH to BOH' },
  { value: 'consumed', label: 'Consumed (composite)' },
];

// Stock badge component
function StockBadge({
  locationName,
  stockBefore,
  stockAfter,
  status,
}: {
  locationName: string | null;
  stockBefore: number | null;
  stockAfter: number | null;
  status: 'on_hand' | 'in_transit' | 'inventory_count' | null;
}) {
  if (!locationName) return <span className="text-muted-foreground">-</span>;

  // Handle special destinations
  if (locationName === 'Composite produced') {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-purple-600 dark:text-purple-400">{locationName}</span>
      </div>
    );
  }

  if (locationName === 'Customer') {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">{locationName}</span>
      </div>
    );
  }

  const statusLabel = status === 'on_hand' ? 'On hand' : status === 'in_transit' ? 'In transit' : 'Inventory count';
  const statusColor = status === 'on_hand' 
    ? 'text-emerald-600 dark:text-emerald-400' 
    : status === 'in_transit' 
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-blue-600 dark:text-blue-400';

  // Helper to format number with red color for negatives
  const formatStock = (value: number) => {
    const isNegative = value < 0;
    return (
      <span className={cn(
        'font-medium',
        isNegative ? 'text-red-600 bg-red-100 dark:bg-red-900/30 px-1 rounded' : ''
      )}>
        {value}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span className="font-medium">{locationName}</span>
        {stockBefore !== null && stockAfter !== null && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-xs font-mono">
            {formatStock(stockBefore)}
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            {formatStock(stockAfter)}
          </span>
        )}
      </div>
      <span className={cn('text-xs', statusColor)}>{statusLabel}</span>
    </div>
  );
}

// User avatar with name
function UserCell({ name, initials }: { name: string | null; initials: string | null }) {
  if (!name) return <span className="text-muted-foreground">-</span>;

  // Generate a consistent color based on initials
  const colors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-amber-500',
    'bg-yellow-500',
    'bg-lime-500',
    'bg-green-500',
    'bg-emerald-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-sky-500',
    'bg-blue-500',
    'bg-indigo-500',
    'bg-violet-500',
    'bg-purple-500',
    'bg-fuchsia-500',
    'bg-pink-500',
  ];
  const colorIndex = (initials?.charCodeAt(0) || 0) % colors.length;
  const bgColor = colors[colorIndex];

  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-7 w-7">
        <AvatarFallback className={cn('text-white text-xs font-medium', bgColor)}>
          {initials || '?'}
        </AvatarFallback>
      </Avatar>
      <span className="text-sm">{name}</span>
    </div>
  );
}

export default function InventoryMovements() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentLocation, locationScope } = useLocationStore();
  const { organization } = useAuth();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  // Real-time subscription for inventory transactions (sellable product movements)
  useEffect(() => {
    if (!currentLocation?.id) return;
    const channel = subscribeToInventoryTransactions(currentLocation.id, () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
    });
    return () => { unsubscribe(channel); };
  }, [currentLocation?.id, queryClient]);

  // Real-time subscription for raw inventory transactions (raw material movements)
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToRawInventoryTransactions(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  // Real-time subscription for goods transfers (inter-location transfers)
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToGoodsTransfers(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  // Real-time subscription for inventory transfers (BOH/FOH movements)
  useEffect(() => {
    if (!currentLocation?.id) return;
    const channel = subscribeToInventoryTransfers(currentLocation.id, () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
    });
    return () => { unsubscribe(channel); };
  }, [currentLocation?.id, queryClient]);

  // Default date range: last 7 days
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [movementType, setMovementType] = useState<MovementType | 'all'>('all');
  const [locationFilter, setLocationFilter] = useState<string>(() =>
    locationScope === 'all' ? 'all' : (currentLocation?.id || 'all')
  );
  const [productFilter, setProductFilter] = useState<string>('all');
  const [itemTypeFilter, setItemTypeFilter] = useState<'all' | 'sellable' | 'raw_goods'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  // Movement detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<InventoryMovement | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [emailReceiptOpen, setEmailReceiptOpen] = useState(false);

  const { locations } = useLocations();
  const { products } = useProducts();

  // Keep local filter in sync with the global location selector in the module header.
  useEffect(() => {
    setLocationFilter(locationScope === 'all' ? 'all' : (currentLocation?.id || 'all'));
  }, [locationScope, currentLocation?.id]);

  // Handle URL params for product filter (when coming from inventory overview)
  useEffect(() => {
    const productParam = searchParams.get('product');
    if (productParam) {
      setProductFilter(productParam);
      // Extend date range when filtering by product
      setDateRange({
        from: subDays(new Date(), 30),
        to: new Date(),
      });
    }
  }, [searchParams]);

  // Build filters
  const filters: MovementFilters = useMemo(() => ({
    movement_type: movementType,
    location_id: locationFilter,
    product_id: productFilter !== 'all' ? productFilter : undefined,
    date_from: dateRange?.from ? startOfDay(dateRange.from).toISOString() : undefined,
    date_to: dateRange?.to ? endOfDay(dateRange.to).toISOString() : undefined,
  }), [movementType, locationFilter, productFilter, dateRange]);

  const { data: movementPage, isLoading, error } = useInventoryMovementsPage({
    filters,
    itemType: itemTypeFilter,
    search: searchQuery,
    page,
    pageSize: MOVEMENTS_PAGE_SIZE,
  });
  const movements = movementPage?.movements || [];
  const totalMovements = movementPage?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalMovements / MOVEMENTS_PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [movementType, locationFilter, productFilter, itemTypeFilter, searchQuery, dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  // Count active filters
  const activeFilterCount = [
    movementType !== 'all',
    locationFilter !== 'all',
    productFilter !== 'all',
    itemTypeFilter !== 'all',
    dateRange?.from || dateRange?.to,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setMovementType('all');
    setLocationFilter('all');
    setProductFilter('all');
    setItemTypeFilter('all');
    setDateRange({
      from: subDays(new Date(), 7),
      to: new Date(),
    });
    setSearchQuery('');
    // Clear URL params
    setSearchParams({});
  };

  // Open movement detail modal
  const openMovementDetail = async (movement: InventoryMovement) => {
    setSelectedMovement(movement);
    setDetailModalOpen(true);
    setLoadingDetail(true);
    setDetailData(null);

    try {
      // Fetch detailed data based on reference type
      if (movement.reference_type === 'goods_transfer' && movement.reference_id) {
        const { data, error } = await supabase
          .from('goods_transfers')
          .select(`
            *,
            from_location:locations!goods_transfers_from_location_id_fkey(id, name),
            to_location:locations!goods_transfers_to_location_id_fkey(id, name),
            creator:profiles!goods_transfers_created_by_fkey(first_name, last_name),
            shipper:profiles!goods_transfers_shipped_by_fkey(first_name, last_name),
            receiver:profiles!goods_transfers_received_by_fkey(first_name, last_name),
            items:goods_transfer_items(
              id,
              quantity_sent,
              quantity_received,
              product:products(id, name, sku),
              raw_item:raw_inventory_items(id, name, sku)
            )
          `)
          .eq('id', movement.reference_id)
          .single();

        if (!error && data) {
          setDetailData({ type: 'goods_transfer', data });
        }
      } else if (movement.reference_type === 'purchase_order' && movement.reference_id) {
        const { data, error } = await supabase
          .from('purchase_orders')
          .select(`
            *,
            supplier:suppliers(id, name),
            location:locations(id, name),
            items:purchase_order_items(
              id,
              quantity_ordered,
              quantity_received,
              unit_cost,
              product:products(id, name, sku),
              raw_item:raw_inventory_items(id, name, sku)
            )
          `)
          .eq('id', movement.reference_id)
          .single();

        if (!error && data) {
          setDetailData({ type: 'purchase_order', data });
        }
      } else if (movement.reference_type === 'order' && movement.reference_id) {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            location:locations(id, name),
            items:order_items(
              id,
              quantity,
              unit_price,
              total_price,
              discount_amount,
              tax_amount,
              notes,
              product:products(id, name, sku)
            ),
            payments(
              id,
              amount,
              payment_method,
              status,
              tip_amount,
              card_brand,
              card_last_four,
              created_at
            ),
            user:profiles(id, first_name, last_name)
          `)
          .eq('id', movement.reference_id)
          .single();

        if (!error && data) {
          setDetailData({ type: 'order', data });
        }
      }
    } catch (error) {
      console.error('Error fetching movement detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Resolve the filtered product name (when navigating from a product page)
  const filteredProduct = productFilter !== 'all'
    ? products.find((p) => p.id === productFilter)
    : null;

  return (
    <div className="pb-24 sm:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {filteredProduct ? (
            <>
              <button
                onClick={() => {
                  setProductFilter('all');
                  searchParams.delete('product');
                  setSearchParams(searchParams, { replace: true });
                }}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-1 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All movements
              </button>
              <h2 className="text-xl font-bold">{filteredProduct.name}</h2>
              <p className="text-sm text-muted-foreground">
                See all inventory movements for this product
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold">Inventory Movements</h2>
              <p className="text-sm text-muted-foreground">
                Track all stock changes across locations
              </p>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Movement Type Filter */}
            <div className="w-48">
              <Select value={movementType} onValueChange={(v) => setMovementType(v as MovementType | 'all')}>
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Movement type" />
                </SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div className="w-48">
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Item Type Filter (Sellable/Raw Goods) */}
            <div className="w-40">
              <Select value={itemTypeFilter} onValueChange={(v) => setItemTypeFilter(v as 'all' | 'sellable' | 'raw_goods')}>
                <SelectTrigger>
                  <SelectValue placeholder="All items" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All items</SelectItem>
                  <SelectItem value="sellable">Sellable</SelectItem>
                  <SelectItem value="raw_goods">Raw Goods</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[280px] justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                      </>
                    ) : (
                      format(dateRange.from, 'MMM d, yyyy')
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                  <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                  showOutsideDays={false}
                />
                <div className="p-3 border-t flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange({ from: subDays(new Date(), 7), to: new Date() })}
                  >
                    Last 7 days
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange({ from: subDays(new Date(), 30), to: new Date() })}
                  >
                    Last 30 days
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange({ from: subMonths(new Date(), 3), to: new Date() })}
                  >
                    Last 3 months
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange({ from: subMonths(new Date(), 6), to: new Date() })}
                  >
                    Last 6 months
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateRange({ from: subMonths(new Date(), 12), to: new Date() })}
                  >
                    Last 12 months
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products, locations, users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-1"
              >
                <X className="h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Product Filter (optional, shown below) */}
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Filter by product:</span>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-64">
                <Package className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {isLoading ? (
            'Loading movements...'
          ) : (
            <>
              Displaying movements from{' '}
              <span className="font-medium text-foreground">
                {dateRange?.from ? format(dateRange.from, 'MMM d, yyyy') : 'all time'}
              </span>
              {dateRange?.to && (
                <>
                  {' '}to{' '}
                  <span className="font-medium text-foreground">
                    {format(dateRange.to, 'MMM d, yyyy')}
                  </span>
                </>
              )}
              {' '}({totalMovements.toLocaleString()} movements)
            </>
          )}
        </p>
      </div>

      {/* Movements Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading inventory movements...</span>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <Activity className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="font-medium text-red-600">Error loading movements</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {(error as Error).message}
          </p>
        </div>
      ) : movements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Activity className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No movements found</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {searchQuery || activeFilterCount > 0
              ? 'Try adjusting your filters'
              : 'No inventory movements recorded in this date range'}
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-4 font-medium text-muted-foreground whitespace-nowrap">Date</th>
                    <th className="text-left p-4 font-medium text-muted-foreground whitespace-nowrap">Movement</th>
                    <th className="text-left p-4 font-medium text-muted-foreground whitespace-nowrap">Product</th>
                    <th className="text-left p-4 font-medium text-muted-foreground whitespace-nowrap">From</th>
                    <th className="text-left p-4 font-medium text-muted-foreground whitespace-nowrap">To</th>
                    <th className="text-left p-4 font-medium text-muted-foreground whitespace-nowrap">User</th>
                    <th className="text-right p-4 font-medium text-muted-foreground whitespace-nowrap">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr
                      key={movement.id}
                      className="border-b last:border-0 hover:bg-muted/50 transition-colors"
                    >
                      {/* Date */}
                      <td className="p-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {format(new Date(movement.date), 'MMM d, yyyy')}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(movement.date), 'h:mm a')}
                          </span>
                        </div>
                      </td>

                      {/* Movement Type - Clickable chip/badge */}
                      <td className="p-4">
                        <button
                          onClick={() => openMovementDetail(movement)}
                          className="text-left cursor-pointer group"
                        >
                          <Badge
                            variant="outline"
                            className={cn(
                              'cursor-pointer transition-all hover:scale-105 hover:shadow-sm',
                              getMovementColor(movement.movement_type)
                            )}
                          >
                            {movement.movement_label}
                          </Badge>
                        </button>
                      </td>

                      {/* Product */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-8 h-8 rounded flex items-center justify-center shrink-0",
                            movement.raw_item_id ? "bg-amber-100 dark:bg-amber-900/30" : "bg-muted"
                          )}>
                            {movement.raw_item_id ? (
                              <Layers className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            ) : (
                              <Package className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          {movement.raw_item_id && movement.raw_item_name ? (
                            <Link
                              to={`/stock-control/raw-items?item=${movement.raw_item_id}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {movement.raw_item_name}
                            </Link>
                          ) : movement.product_id && movement.product_name ? (
                            <Link
                              to={`/stock-control/overview?product=${movement.product_id}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {movement.product_name}
                            </Link>
                          ) : (
                            <span className="font-medium text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>

                      {/* From */}
                      <td className="p-4">
                        <StockBadge
                          locationName={movement.from_location_name}
                          stockBefore={movement.from_stock_before}
                          stockAfter={movement.from_stock_after}
                          status={movement.from_status}
                        />
                      </td>

                      {/* To */}
                      <td className="p-4">
                        <StockBadge
                          locationName={movement.to_location_name}
                          stockBefore={movement.to_stock_before}
                          stockAfter={movement.to_stock_after}
                          status={movement.to_status}
                        />
                      </td>

                      {/* User */}
                      <td className="p-4">
                        <UserCell
                          name={movement.user_name}
                          initials={movement.user_initials}
                        />
                      </td>

                      {/* Quantity */}
                      <td className="p-4 text-right">
                        <span className="font-semibold tabular-nums text-lg">
                          {movement.quantity.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && totalMovements > 0 && (
        <div className="flex flex-col gap-3 border-t pt-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground">Showing {((page - 1) * MOVEMENTS_PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * MOVEMENTS_PAGE_SIZE, totalMovements).toLocaleString()} of {totalMovements.toLocaleString()} movements</p>
          <div className="flex items-center gap-2"><Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((current) => current - 1)}><ArrowLeft className="mr-1 h-4 w-4" />Previous</Button><span className="min-w-20 text-center text-muted-foreground">Page {page} of {totalPages}</span><Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next<ArrowRight className="ml-1 h-4 w-4" /></Button></div>
        </div>
      )}

      {/* Movement Detail Modal */}
      <ResponsiveModal open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <ResponsiveModalContent maxWidth="2xl">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {detailData?.type === 'goods_transfer' && detailData.data && (
                <>
                  {detailData.data.transfer_number} - {format(new Date(detailData.data.expected_date || detailData.data.created_at), 'EEE, MMM d, yyyy')}
                  <Badge
                    variant={
                      detailData.data.status === 'received' ? 'success' :
                      detailData.data.status === 'shipped' || detailData.data.status === 'in_transit' ? 'warning' :
                      'outline'
                    }
                    className="ml-2"
                  >
                    {detailData.data.status === 'received' ? 'Received' :
                     detailData.data.status === 'shipped' || detailData.data.status === 'in_transit' ? 'In Transit' :
                     detailData.data.status === 'partially_received' ? 'Partially Received' :
                     detailData.data.status.charAt(0).toUpperCase() + detailData.data.status.slice(1)}
                  </Badge>
                </>
              )}
              {detailData?.type === 'purchase_order' && detailData.data && (
                <>Purchase Order - {format(new Date(detailData.data.created_at), 'MMM d, yyyy')}</>
              )}
              {detailData?.type === 'order' && detailData.data && (
                <div className="flex items-center gap-2">
                  <span>Sale #{detailData.data.order_number}</span>
                  <Badge
                    variant={
                      detailData.data.status === 'completed' ? 'success' :
                      detailData.data.status === 'voided' ? 'destructive' :
                      detailData.data.status === 'refunded' ? 'warning' :
                      'outline'
                    }
                  >
                    {detailData.data.status ? detailData.data.status.charAt(0).toUpperCase() + detailData.data.status.slice(1) : 'Unknown'}
                  </Badge>
                </div>
              )}
              {!detailData && selectedMovement && (
                <>{selectedMovement.movement_label}</>
              )}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {selectedMovement && format(new Date(selectedMovement.date), 'MMMM d, yyyy \'at\' h:mm a')}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            {loadingDetail ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : detailData?.type === 'order' && detailData.data ? (
              /* ============================================
                 Sale / Order Detail - Lightspeed style layout
                 ============================================ */
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left side: Sale details */}
                <div className="flex-1 min-w-0 space-y-4">
                  {/* Sale header info */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Receipt</p>
                      <p className="font-medium">{detailData.data.order_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Date</p>
                      <p className="font-medium">{format(new Date(detailData.data.created_at), 'MMM d, yyyy \'at\' h:mm a')}</p>
                    </div>
                    {detailData.data.user && (
                      <div>
                        <p className="text-muted-foreground">Sold by</p>
                        <p className="font-medium">
                          {detailData.data.user.first_name} {detailData.data.user.last_name}
                        </p>
                      </div>
                    )}
                    {detailData.data.location && (
                      <div>
                        <p className="text-muted-foreground">Location</p>
                        <p className="font-medium">{detailData.data.location.name}</p>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Sale details label */}
                  <h4 className="text-sm font-semibold text-primary">Sale details</h4>

                  {/* Line items */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-primary/5">
                          <th className="text-left px-4 py-2 font-medium w-8">#</th>
                          <th className="text-left px-4 py-2 font-medium">Product</th>
                          <th className="text-left px-4 py-2 font-medium">Details</th>
                          <th className="text-right px-4 py-2 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.data.items?.map((item: any, idx: number) => (
                          <tr key={item.id} className="border-b last:border-0">
                            <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
                            <td className="px-4 py-3">
                              {item.product ? (
                                <Link
                                  to={`/stock-control/overview?product=${item.product.id}`}
                                  className="text-primary hover:underline font-medium"
                                >
                                  {item.product.name}
                                </Link>
                              ) : (
                                <span className="font-medium">Unknown product</span>
                              )}
                              {selectedMovement?.user_name && (
                                <p className="text-xs text-muted-foreground">Sold by: {selectedMovement.user_name}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                              <span>@ {formatCurrency(item.unit_price || 0)}</span>
                              {item.tax_amount !== null && item.tax_amount !== undefined && (
                                <span className="ml-1">
                                  + {formatCurrency(item.tax_amount)} Tax
                                  {item.tax_amount === 0 && ' (No Tax)'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium tabular-nums">
                              {formatCurrency(item.total_price || (item.unit_price * item.quantity) || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Notes */}
                  {detailData.data.notes && (
                    <div className="flex gap-2 text-sm">
                      <span className="text-muted-foreground">Note</span>
                      <span>{detailData.data.notes}</span>
                    </div>
                  )}

                  <Separator />

                  {/* Sale totals */}
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium tabular-nums">{formatCurrency(detailData.data.subtotal || 0)}</span>
                    </div>
                    {detailData.data.discount_amount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span className="font-medium tabular-nums">-{formatCurrency(detailData.data.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Total tax {detailData.data.tax_amount === 0 ? '(No Tax)' : ''}
                      </span>
                      <span className="font-medium tabular-nums">{formatCurrency(detailData.data.tax_amount || 0)}</span>
                    </div>
                    {detailData.data.tip_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tip</span>
                        <span className="font-medium tabular-nums">{formatCurrency(detailData.data.tip_amount)}</span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Sale total */}
                  <div className="flex justify-between items-center text-base font-semibold">
                    <span>SALE TOTAL</span>
                    <span className="tabular-nums">{formatCurrency(detailData.data.total || 0)}</span>
                  </div>

                  <Separator />

                  {/* Payments */}
                  {detailData.data.payments && detailData.data.payments.length > 0 && (
                    <div className="space-y-2 text-sm">
                      {detailData.data.payments.map((payment: any) => (
                        <div key={payment.id} className="flex justify-between items-start">
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <span className="font-medium uppercase">
                                {payment.payment_method}
                                {payment.card_brand && ` (${payment.card_brand}`}
                                {payment.card_last_four && ` ****${payment.card_last_four}`}
                                {payment.card_brand && ')'}
                              </span>
                              {payment.created_at && (
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(payment.created_at), 'EEE, MMM d, yyyy \'at\' h:mm a')}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="font-medium tabular-nums">{formatCurrency(payment.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Balance */}
                  {detailData.data.payments && detailData.data.payments.length > 0 && (
                    <>
                      <Separator />
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-medium">Balance</span>
                        <span className="font-medium tabular-nums">
                          {formatCurrency(
                            (detailData.data.total || 0) -
                            detailData.data.payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0)
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Right side: Action buttons */}
                <div className="md:w-48 shrink-0 space-y-2">
                  <Button
                    variant="default"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      // Navigate to the order/sale for return
                      setDetailModalOpen(false);
                      navigate(`/pos?return=${detailData.data.id}`);
                    }}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Return Items
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setDetailModalOpen(false);
                      navigate(`/pos?edit=${detailData.data.id}`);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      window.print();
                    }}
                  >
                    <Gift className="h-4 w-4" />
                    Gift receipt
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setEmailReceiptOpen(true);
                    }}
                  >
                    <Mail className="h-4 w-4" />
                    Email receipt
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      window.print();
                    }}
                  >
                    <Printer className="h-4 w-4" />
                    Print receipt
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                    disabled={detailData.data.status === 'voided'}
                    onClick={async () => {
                      if (!confirm('Are you sure you want to void this sale? This cannot be undone.')) return;
                      const { error } = await supabase
                        .from('orders')
                        .update({ status: 'voided' })
                        .eq('id', detailData.data.id);
                      if (!error) {
                        queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
                        setDetailModalOpen(false);
                      }
                    }}
                  >
                    <Ban className="h-4 w-4" />
                    {detailData.data.status === 'voided' ? 'Voided' : 'Void'}
                  </Button>
                </div>
              </div>
            ) : detailData?.type === 'goods_transfer' && detailData.data ? (
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left side: Transfer details */}
                <div className="flex-1 min-w-0 space-y-6">
                  {/* Transfer info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Order from</p>
                      <p className="font-medium">{detailData.data.from_location?.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Deliver to</p>
                      <p className="font-medium">{detailData.data.to_location?.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Created</p>
                      <p className="font-medium">{format(new Date(detailData.data.created_at), 'd MMM yyyy')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Created by</p>
                      <p className="font-medium">
                        {detailData.data.creator?.first_name} {detailData.data.creator?.last_name}
                      </p>
                    </div>
                    {detailData.data.expected_date && (
                      <div>
                        <p className="text-muted-foreground">Delivery due</p>
                        <p className="font-medium">{format(new Date(detailData.data.expected_date), 'd MMM yyyy')}</p>
                      </div>
                    )}
                    {detailData.data.received_at && (
                      <div>
                        <p className="text-muted-foreground">Received</p>
                        <p className="font-medium">{format(new Date(detailData.data.received_at), 'd MMM yyyy')}</p>
                      </div>
                    )}
                  </div>

                  {/* Timeline */}
                  {(detailData.data.shipped_at || detailData.data.received_at) && (
                    <div className="flex flex-wrap gap-4 text-sm border-t pt-4">
                      {detailData.data.shipped_at && (
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            Shipped by{' '}
                            <span className="font-medium text-foreground">
                              {detailData.data.shipper?.first_name} {detailData.data.shipper?.last_name}
                            </span>{' '}
                            on {format(new Date(detailData.data.shipped_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                      {detailData.data.received_at && (
                        <div className="flex items-center gap-2">
                          <PackageCheck className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            Received by{' '}
                            <span className="font-medium text-foreground">
                              {detailData.data.receiver?.first_name} {detailData.data.receiver?.last_name}
                            </span>{' '}
                            on {format(new Date(detailData.data.received_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Items table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-amber-50 dark:bg-amber-900/20">
                          <th className="text-left px-4 py-2 font-medium">Line</th>
                          <th className="text-left px-4 py-2 font-medium">Product</th>
                          <th className="text-left px-4 py-2 font-medium">SKU</th>
                          <th className="text-right px-4 py-2 font-medium">Shipped</th>
                          <th className="text-right px-4 py-2 font-medium">Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.data.items?.map((item: any, idx: number) => (
                          <tr key={item.id} className="border-b last:border-0">
                            <td className="px-4 py-2">{idx + 1}</td>
                            <td className="px-4 py-2">
                              {item.product ? (
                                <Link
                                  to={`/stock-control/overview?product=${item.product.id}`}
                                  className="text-primary hover:underline"
                                >
                                  {item.product.name}
                                </Link>
                              ) : item.raw_item ? (
                                <Link
                                  to={`/stock-control/raw-items?item=${item.raw_item.id}`}
                                  className="text-primary hover:underline"
                                >
                                  {item.raw_item.name}
                                </Link>
                              ) : (
                                <span>Unknown</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">
                              {item.product?.sku || item.raw_item?.sku || '-'}
                            </td>
                            <td className="px-4 py-2 text-right font-medium tabular-nums">
                              {item.quantity_sent || 0}
                            </td>
                            <td className="px-4 py-2 text-right font-medium tabular-nums">
                              {item.quantity_received || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-amber-50 dark:bg-amber-900/20 font-medium">
                          <td colSpan={3} className="px-4 py-2">Total</td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {detailData.data.items?.reduce((sum: number, item: any) => sum + (item.quantity_sent || 0), 0)}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {detailData.data.items?.reduce((sum: number, item: any) => sum + (item.quantity_received || 0), 0)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Notes */}
                  {(detailData.data.notes || detailData.data.shipping_notes || detailData.data.receiving_notes) && (
                    <div className="text-sm border-t pt-4">
                      <p className="text-muted-foreground mb-1">Notes</p>
                      <p>{detailData.data.notes || detailData.data.shipping_notes || detailData.data.receiving_notes}</p>
                    </div>
                  )}
                </div>

                {/* Right side: Action buttons for transfer */}
                <div className="md:w-48 shrink-0 space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setDetailModalOpen(false);
                      navigate(`/stock-control/goods-transfers/${detailData.data.id}`);
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                    View transfer
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <FileText className="h-4 w-4" />
                    Export CSV
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>
              </div>
            ) : detailData?.type === 'purchase_order' && detailData.data ? (
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left side: PO details */}
                <div className="flex-1 min-w-0 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    {detailData.data.supplier && (
                      <div>
                        <p className="text-muted-foreground">Supplier</p>
                        <p className="font-medium">{detailData.data.supplier.name}</p>
                      </div>
                    )}
                    {detailData.data.location && (
                      <div>
                        <p className="text-muted-foreground">Location</p>
                        <p className="font-medium">{detailData.data.location.name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <Badge variant="outline">
                        {detailData.data.status ? detailData.data.status.charAt(0).toUpperCase() + detailData.data.status.slice(1) : 'Unknown'}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  {/* PO Items */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-purple-50 dark:bg-purple-900/20">
                          <th className="text-left px-4 py-2 font-medium">#</th>
                          <th className="text-left px-4 py-2 font-medium">Product</th>
                          <th className="text-right px-4 py-2 font-medium">Ordered</th>
                          <th className="text-right px-4 py-2 font-medium">Received</th>
                          <th className="text-right px-4 py-2 font-medium">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.data.items?.map((item: any, idx: number) => (
                          <tr key={item.id} className="border-b last:border-0">
                            <td className="px-4 py-2 text-muted-foreground">{idx + 1}</td>
                            <td className="px-4 py-2">
                              {item.product ? (
                                <Link
                                  to={`/stock-control/overview?product=${item.product.id}`}
                                  className="text-primary hover:underline font-medium"
                                >
                                  {item.product.name}
                                </Link>
                              ) : item.raw_item ? (
                                <Link
                                  to={`/stock-control/raw-items?item=${item.raw_item.id}`}
                                  className="text-primary hover:underline font-medium"
                                >
                                  {item.raw_item.name}
                                </Link>
                              ) : (
                                <span className="font-medium">Unknown</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">{item.quantity_ordered || 0}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{item.quantity_received || 0}</td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {item.unit_cost ? formatCurrency(item.unit_cost) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {detailData.data.total && (
                    <>
                      <Separator />
                      <div className="flex justify-between items-center font-semibold">
                        <span>Total</span>
                        <span className="tabular-nums">{formatCurrency(detailData.data.total)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Right side: Action buttons for PO */}
                <div className="md:w-48 shrink-0 space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setDetailModalOpen(false);
                      navigate(`/stock-control/purchase-orders/${detailData.data.id}`);
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                    View PO
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>
              </div>
            ) : selectedMovement ? (
              /* ============================================
                 Generic movement detail (adjustments, waste, counts, BOH/FOH, etc.)
                 ============================================ */
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left side: Movement details */}
                <div className="flex-1 min-w-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Movement Type</p>
                      <Badge
                        variant="outline"
                        className={cn('font-medium', getMovementColor(selectedMovement.movement_type))}
                      >
                        {selectedMovement.movement_label}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Quantity</p>
                      <p className="font-medium text-lg tabular-nums">{selectedMovement.quantity.toLocaleString()}</p>
                    </div>
                    {(selectedMovement.product_name || selectedMovement.raw_item_name) && (
                      <div>
                        <p className="text-muted-foreground">
                          {selectedMovement.raw_item_id ? 'Raw Material' : 'Product'}
                        </p>
                        {selectedMovement.raw_item_id ? (
                          <Link
                            to={`/stock-control/raw-items?item=${selectedMovement.raw_item_id}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {selectedMovement.raw_item_name}
                          </Link>
                        ) : selectedMovement.product_id ? (
                          <Link
                            to={`/stock-control/overview?product=${selectedMovement.product_id}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {selectedMovement.product_name}
                          </Link>
                        ) : (
                          <p className="font-medium">{selectedMovement.product_name || selectedMovement.raw_item_name}</p>
                        )}
                      </div>
                    )}
                    {selectedMovement.user_name && (
                      <div>
                        <p className="text-muted-foreground">User</p>
                        <p className="font-medium">{selectedMovement.user_name}</p>
                      </div>
                    )}
                    {selectedMovement.from_location_name && (
                      <div>
                        <p className="text-muted-foreground">From</p>
                        <p className="font-medium">{selectedMovement.from_location_name}</p>
                        {selectedMovement.from_stock_before !== null && selectedMovement.from_stock_after !== null && (
                          <p className="text-xs text-muted-foreground tabular-nums">
                            Stock: {selectedMovement.from_stock_before} → {selectedMovement.from_stock_after}
                          </p>
                        )}
                      </div>
                    )}
                    {selectedMovement.to_location_name && (
                      <div>
                        <p className="text-muted-foreground">To</p>
                        <p className="font-medium">{selectedMovement.to_location_name}</p>
                        {selectedMovement.to_stock_before !== null && selectedMovement.to_stock_after !== null && (
                          <p className="text-xs text-muted-foreground tabular-nums">
                            Stock: {selectedMovement.to_stock_before} → {selectedMovement.to_stock_after}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedMovement.notes && (
                    <div className="text-sm border-t pt-4">
                      <p className="text-muted-foreground mb-1">Notes</p>
                      <p>{selectedMovement.notes}</p>
                    </div>
                  )}
                </div>

                {/* Right side: Action buttons for generic movements */}
                <div className="md:w-48 shrink-0 space-y-2">
                  {selectedMovement.raw_item_id && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setDetailModalOpen(false);
                        navigate(`/stock-control/raw-items?item=${selectedMovement.raw_item_id}`);
                      }}
                    >
                      <Layers className="h-4 w-4" />
                      View raw material
                    </Button>
                  )}
                  {selectedMovement.product_id && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setDetailModalOpen(false);
                        navigate(`/stock-control/overview?product=${selectedMovement.product_id}`);
                      }}
                    >
                      <Package className="h-4 w-4" />
                      View in inventory
                    </Button>
                  )}
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>
              </div>
            ) : null}
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Email Receipt Dialog */}
      <EmailReceiptDialog
        open={emailReceiptOpen}
        onOpenChange={setEmailReceiptOpen}
        order={detailData?.type === 'order' ? detailData.data : null}
      />
    </div>
  );
}
